import React, { useMemo } from 'react'
import { CircularProgress, Grid } from '@material-ui/core'
import useAIScanAnalysis from './useAIScanAnalysis'
import { useTranslation } from 'react-i18next'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { AnalysisTypes } from './aiScanAnalysisConsts'
import AnalysisType from './AnalysisType/AnalysisType'
import IPRAlignerReachedIcon from './icons/IPRAlignerReachedIcon'
import LastAlignerReachedIcon from './icons/LastAlignerReachedIcon'
import { makeStyles } from '@material-ui/styles'

const useStyles = makeStyles(theme => ({
  divider: {
    height: 1,
    backgroundColor: '#F6F6F6',
    marginBottom: 16
  },
  loader: {
    marginBottom: 10,
    borderRadius: 10,
    backgroundColor: '#F6F6F6',
    minHeight: 80
  }
}))

const AIScanAnalysis = ({ scanId }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const {
    isLoading,
    scanOverallTrackingStatus,
    txTrackerAnalysisMessage,
    oralHygieneAnalysis,
    oralHygieneAnalysisMessage,
    IPRAlignerReached,
    lastAlignerReached,
    analysisThemeByType,
    analysisThemes
  } = useAIScanAnalysis({ scanId })

  const analysisTypeProps = useMemo(
    () =>
      [
        {
          type: AnalysisTypes.TxTracker,
          title: t('pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.txTracker.title'),
          theme: analysisThemes[analysisThemeByType[AnalysisTypes.TxTracker][scanOverallTrackingStatus]],
          messages: [{ message: txTrackerAnalysisMessage, condition: true }],
          condition: () => !!scanOverallTrackingStatus
        },
        {
          type: AnalysisTypes.OralHygiene,
          title: t('pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.oralHygiene.title'),
          theme: analysisThemes[analysisThemeByType[AnalysisTypes.OralHygiene][oralHygieneAnalysis]],
          messages: [{ message: oralHygieneAnalysisMessage, condition: true }],
          condition: () => !!oralHygieneAnalysis
        },
        {
          type: AnalysisTypes.Aligners,
          title: t('pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.aligners.title'),
          theme: analysisThemes[AnalysisTypes.Aligners],
          messages: [
            {
              icon: <IPRAlignerReachedIcon />,
              message: t('pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.aligners.IPRReached'),
              condition: IPRAlignerReached
            },
            {
              icon: <LastAlignerReachedIcon />,
              message: t('pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.aligners.lastAlignerReached'),
              condition: lastAlignerReached
            }
          ],
          condition: () => IPRAlignerReached || lastAlignerReached
        }
      ].filter(analysisType => analysisType.condition()),
    [
      IPRAlignerReached,
      analysisThemeByType,
      analysisThemes,
      lastAlignerReached,
      oralHygieneAnalysis,
      oralHygieneAnalysisMessage,
      scanOverallTrackingStatus,
      t,
      txTrackerAnalysisMessage
    ]
  )

  return isLoading ? (
    <Grid container justifyContent="center" alignItems="center" className={classes.loader}>
      <Grid item>
        <CircularProgress size={18} />
      </Grid>
    </Grid>
  ) : (
    analysisTypeProps.length > 0 && (
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <DazzedParagraph14 bold>
            {t('pages.patients.selectedPatient.hiTagScan.aiScanAnalysis.title')}
          </DazzedParagraph14>
        </Grid>
        <Grid item>
          <Grid container direction="column" wrap="nowrap">
            {analysisTypeProps.map(({ type, title, theme, messages }) => (
              <AnalysisType key={type} type={type} title={title} theme={theme} messages={messages} />
            ))}
          </Grid>
        </Grid>
        <Grid item className={classes.divider}></Grid>
      </Grid>
    )
  )
}

export default AIScanAnalysis
